import { useGetArtist } from '@hooks/TRPC_Hooks/useGetArtist'
import { useGetCollectedToyInstances } from '@hooks/TRPC_Hooks/useGetCollectedToyInstances'
import { useGetCollectionEarningsCount } from '@hooks/TRPC_Hooks/useGetCollectionEarningsCount'
import { useGetSingleCollection } from '@hooks/TRPC_Hooks/useGetSingleCollection'
import { useGetStoreItems } from '@hooks/TRPC_Hooks/useGetStoreItems'
import { useGetUserStoreItems } from '@hooks/TRPC_Hooks/useGetUserStoreItems'
import { useIsCollectionBonusToyCollected } from '@hooks/TRPC_Hooks/useIsCollectionBonusToyCollected'
import { useProfile } from '@hooks/TRPC_Hooks/useProfile'
import { useToyActiveDrops } from '@hooks/TRPC_Hooks/useToyActiveDrops'
import { DropMechanics, toy } from '@specs/specs'
import { useSession } from 'next-auth/react'
import { useRouter } from 'next/router'
import { useMemo } from 'react'
import { haveAvailableToys } from '@specs/helpers'
import { useGetBonusToy } from '@hooks/TRPC_Hooks/useGetBonusToy'
import { PageQueries } from '@specs/navigation'
import { useGetToy } from '@hooks/TRPC_Hooks/useGetToy'
import { getSanitizedCode } from '@services/toys/helpers'
import { CollaboratorsType } from '@services/social/types'

interface getToyDataProps {
    toyId: toy['id']
}

export const useGetToyDataOnPage = ({ toyId }: getToyDataProps) => {
    const router = useRouter()
    const { status } = useSession()
    const { secret }: PageQueries = router.query
    const isSecret = typeof secret === 'string'

    const { data: toy, isLoading: isLoadingToy } = useGetToy({ id: toyId })
    const collectionId = toy?.asCollectionBonus?.[0]?.collectionid ?? toy?.collections?.[0]?.collectionid
    const IsEnabledCollectionDataFetch = typeof collectionId === 'number'
    const artistId = toy?.authorid
    const { data: profile, isLoading: isProfileLoading } = useProfile()
    const { data: toyInstances, isLoading: isInstancesLoading } = useGetCollectedToyInstances({ toyId })
    const { data: stores, isLoading: isStoreLoading } = useGetStoreItems({ limit: 1, toyId })
    const { data: myStores, isLoading: isMyStoresLoading } = useGetUserStoreItems({ toyId })

    const { data: artist, isLoading: artistLoading } = useGetArtist({ id: artistId ?? -1 }, { enabled: typeof artistId === 'number' })
    const iAmTheCreator = typeof artist?.profile?.shortid === 'string' && typeof profile?.shortid === 'string' ? artist.profile.shortid === profile.shortid : false

    const { data: drops, isLoading: isDropsLoading } = useToyActiveDrops({ toyId, secretCode: getSanitizedCode(secret) }, { enabled: !iAmTheCreator })

    // TODO: Костыль. Поменять на нормальную механику обработки всех типов дропов.
    const currentDrop = drops?.find(({ mechanic }) => (isSecret || iAmTheCreator ? mechanic === DropMechanics.SECRET_PERSONAL || mechanic === DropMechanics.SECRET : true))

    const { data: collection, isLoading: collectionLoading } = useGetSingleCollection(
        { id: collectionId ?? -1 },
        {
            enabled: IsEnabledCollectionDataFetch,
        },
    )

    const { data: bonusToy, isLoading: bonusToyLoading } = useGetBonusToy(
        { id: collectionId ?? -1 },
        {
            enabled: IsEnabledCollectionDataFetch,
        },
    )
    const isBonusToy = bonusToy?.toyid === toyId

    const { data: collected, isLoading: collectedLoading } = useIsCollectionBonusToyCollected(
        { toyId },
        {
            enabled: isBonusToy,
        },
    )

    const { data: earned, isLoading: earnedLoading } = useGetCollectionEarningsCount(
        { collectionId: collectionId ?? -1 },
        {
            enabled: !!collection,
        },
    )

    const currentProfileId = useMemo(() => profile?.id, [profile])

    const dropMechanic = currentDrop?.mechanic
    const isSecretDrop = typeof dropMechanic === 'undefined' ? false : dropMechanic === DropMechanics.SECRET || dropMechanic === DropMechanics.SECRET_PERSONAL
    const isArtClubDrop = dropMechanic === DropMechanics.ARTCLUB

    const isCollectedBonusToy = isBonusToy && !!collected
    // TODO: Костыль. Проверка на наличие фиугрки в toyInstances.dropOwnerships не дает возможности коллекта. Надо видоизменить флоу работы с instanceId
    const isCollectedCurrentDrop =
        typeof currentDrop === 'undefined'
            ? false
            : dropMechanic === DropMechanics.BUY || dropMechanic === DropMechanics.SECRET_PERSONAL
              ? false
              : !!currentDrop.earnings?.some(({ profileid }) => profileid === currentProfileId) || !!toyInstances?.dropOwnerships?.some(({ profileid }) => profileid === currentProfileId)
    const isCollected = isCollectedBonusToy || isCollectedCurrentDrop
    const isHaveToyInCollection = !!toyInstances && (toyInstances.dropOwnerships.some(({ profileid }) => profileid === currentProfileId) || toyInstances.ownerships.some(({ profileid }) => profileid === currentProfileId))
    const isArtClubMember = !!profile?.isArtClubMember

    const isCollectableDrop = typeof currentDrop === 'undefined' ? false : isCollectedCurrentDrop ? false : isSecretDrop ? isSecret : true
    const isCollectableBonusToy = !isBonusToy ? false : !isCollectedBonusToy && (typeof earned === 'number' && typeof collection?.toysTotal === 'number' ? earned === collection.toysTotal : false)
    const isCollectablePartnerGift = !!toy?.badges?.some((badge) => badge === 'digitalTwin')
    const isCollectable = haveAvailableToys(toy) && (isCollectableDrop || isCollectableBonusToy || isCollectablePartnerGift)

    const isLoading =
        isLoadingToy ||
        (status === 'authenticated' && (isInstancesLoading || isStoreLoading || isProfileLoading)) ||
        (status === 'authenticated' && iAmTheCreator && isDropsLoading) ||
        (status === 'authenticated' && isMyStoresLoading) ||
        (status === 'authenticated' && !!collection && earnedLoading) ||
        (status === 'authenticated' && isBonusToy && collectedLoading) ||
        (IsEnabledCollectionDataFetch && (collectionLoading || bonusToyLoading)) ||
        (!!artistId && artistLoading)

    const firstStoreItem = stores?.items?.[0]
    const myFirstStoreItem = myStores?.[0]
    const haveFigurinesForSale = (toyInstances?.ownerships?.length ?? 0) > (myStores?.length ?? 0)

    const artists: CollaboratorsType = artist
        ? [
              {
                  id: artist.id,
                  profile: {
                      shortid: artist.profile?.shortid,
                      description: artist.profile?.description,
                  },
              },
              ...(toy?.collaborators ?? []),
          ]
        : []

    return {
        haveFigurinesForSale,
        firstStoreItem,
        myFirstStoreItem,
        collection,
        currentDrop,
        artist,
        artists,
        isArtClub: isArtClubDrop,
        isCollectable,
        isArtClubMember,
        isLoading,
        toy,
        isBonusToy,
        isHaveToyInCollection,
        isCollected,
        iAmTheCreator,
        earnFromCollectionCount: earned,
    }
}
